import React, { useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { getIn } from "formik";
import { useSelector } from "react-redux";

import GoogleMaps from "../../../../../GoogleMaps";
import {
  getLatLngFromAddress,
  getAddressFromLatLng,
} from "../../../../../../utils/Geocode";
import { UITextField, UINumberField, UIIcon } from "../../../../../ui";
import { getVisibleError } from "../helpers";
import messages from "../../../../../../assets/locale/messages";
import Card from "../../../../../Card";

const TrainingLocationFields = ({
  formik,
  baseName,
  mode,
  defaultBoundaryMetersFromEvent,
  interactionsDisabled,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const event = useSelector((state) => state.events?.event);
  const { events } = messages[lang] ?? messages.en ?? {};
  const creatEvent = events?.creatEvent;
  const t = creatEvent?.defineRolesAndShifts;
  const labels = creatEvent?.labels;
  const ph = creatEvent?.placeholders;
  const basicInfo = creatEvent?.basicInformation;

  const geocodeTimerRef = useRef(null);
  const initializedRef = useRef(false);
  const appliedSuggestedBoundaryRef = useRef({});
  const setFieldValue = formik?.setFieldValue;

  const trainingLat = getIn(formik?.values ?? {}, `${baseName}.lat`);
  const trainingLng = getIn(formik?.values ?? {}, `${baseName}.lng`);
  const trainingRadius = getIn(
    formik?.values ?? {},
    `${baseName}.location_radius`,
  );

  const hasLatLng =
    trainingLat != null &&
    trainingLat !== "" &&
    trainingLng != null &&
    trainingLng !== "";
  const computedFallbackBoundary =
    defaultBoundaryMetersFromEvent != null &&
    `${defaultBoundaryMetersFromEvent}`.trim() !== ""
      ? String(defaultBoundaryMetersFromEvent).trim()
      : null;
  const locationRadius =
    trainingRadius ?? computedFallbackBoundary ?? event?.event_radius ?? "";

  useEffect(() => {
    initializedRef.current = false;
    delete appliedSuggestedBoundaryRef.current[baseName];
  }, [baseName]);

  useEffect(() => {
    if (!hasLatLng) initializedRef.current = false;
  }, [hasLatLng]);

  useEffect(() => {
    if (mode !== "training" || !setFieldValue) return;
    if (!computedFallbackBoundary) return;
    const current = getIn(formik?.values ?? {}, `${baseName}.location_radius`);
    if (current != null && String(current).trim() !== "") return;
    if (appliedSuggestedBoundaryRef.current[baseName]) return;

    appliedSuggestedBoundaryRef.current[baseName] = true;
    setFieldValue(
      `${baseName}.location_radius`,
      computedFallbackBoundary,
      false,
    );
  }, [baseName, computedFallbackBoundary, mode, setFieldValue, formik]);

  useEffect(() => () => clearTimeout(geocodeTimerRef.current), []);

  /** Only typed addresses are geocoded; stored or pin-derived ones must not move the saved coordinates. */
  const scheduleGeocode = (value) => {
    clearTimeout(geocodeTimerRef.current);
    geocodeTimerRef.current = setTimeout(() => {
      getLatLngFromAddress(
        value,
        (lat, lng) => {
          setFieldValue(`${baseName}.lat`, lat);
          setFieldValue(`${baseName}.lng`, lng);
        },
        (error) => {
          console.error(error);
          setFieldValue(`${baseName}.lat`, null);
          setFieldValue(`${baseName}.lng`, null);
        },
      );
    }, 600);
  };

  const updateAddressFromCoords = useCallback(
    (lat, lng) => {
      if (!setFieldValue) return;
      getAddressFromLatLng(lat, lng, (addr) => {
        setFieldValue(`${baseName}.location_address`, addr || "");
      });
    },
    [baseName, setFieldValue],
  );

  const handleMarkerMove = useCallback(
    (position) => {
      if (interactionsDisabled) return;
      if (!setFieldValue || !position?.coords) return;
      const { lat, lng } = position.coords;
      setFieldValue(`${baseName}.lat`, lat);
      setFieldValue(`${baseName}.lng`, lng);
      updateAddressFromCoords(lat, lng);
    },
    [baseName, setFieldValue, updateAddressFromCoords, interactionsDisabled],
  );

  useEffect(() => {
    if (!setFieldValue || !hasLatLng || initializedRef.current) return;
    initializedRef.current = true;
    updateAddressFromCoords(trainingLat, trainingLng);
  }, [
    setFieldValue,
    hasLatLng,
    trainingLat,
    trainingLng,
    updateAddressFromCoords,
  ]);

  if (mode !== "training" || !t || !setFieldValue || !formik) return null;

  return (
    <div className="training-location-fields">
      <div className="roles-field-grid roles-field-grid--two">
        <UITextField
          label={t.trainingLocation}
          name={`${baseName}.location_address`}
          placeholder={ph?.trainingLocation}
          required
          disabled={interactionsDisabled}
          helperText={
            getVisibleError(formik, `${baseName}.location_address`) ||
            t.trainingLocationHelper
          }
          onValueChange={(value) => {
            if (interactionsDisabled) return;
            if (!value) {
              clearTimeout(geocodeTimerRef.current);
              setFieldValue(`${baseName}.lat`, null);
              setFieldValue(`${baseName}.lng`, null);
              return;
            }
            scheduleGeocode(value);
          }}
        />
        <UINumberField
          label={labels?.locationBoundaries ?? labels?.diameter}
          name={`${baseName}.location_radius`}
          min={0}
          required
          placeholder={ph?.trainingLocationBoundariesPlaceholder}
          disabled={interactionsDisabled}
          helperText={
            getVisibleError(formik, `${baseName}.location_radius`) ||
            labels?.trainingLocationBoundariesHelper
          }
        />
      </div>

      <Card
        title={
          <>
            <UIIcon
              name="locationPinOutlined"
              size="md"
              color="var(--color-primary-900)"
              className="training-location-fields__map-title-icon"
            />

            <span className="text-primary-900">
              {basicInfo?.locationAndBoundaries}
            </span>
          </>
        }
      >
        <GoogleMaps
          getCoords={handleMarkerMove}
          className="my-3"
          isMarker={true}
          showCircle={!!locationRadius}
          circleRadius={+locationRadius || 0}
          wrapperClasses="min-height-map mb-4"
          markerMoved={handleMarkerMove}
          viewOnly={interactionsDisabled}
          updatedPosition={
            hasLatLng
              ? { coords: { lat: trainingLat, lng: trainingLng } }
              : undefined
          }
          savedCoords={
            hasLatLng
              ? { coords: { lat: trainingLat, lng: trainingLng } }
              : event?.lat && event?.lng
              ? { coords: { lat: event.lat, lng: event.lng } }
              : undefined
          }
        />
      </Card>
    </div>
  );
};

TrainingLocationFields.propTypes = {
  formik: PropTypes.object.isRequired,
  baseName: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["shift", "training"]),
  defaultBoundaryMetersFromEvent: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  interactionsDisabled: PropTypes.bool,
};

TrainingLocationFields.defaultProps = {
  mode: "training",
  defaultBoundaryMetersFromEvent: undefined,
  interactionsDisabled: false,
};

export default TrainingLocationFields;
